import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useMe } from '../api/auth';
import { ApiError } from '../api/client';
import { useChangeMyPassword, usePatchMe } from '../api/users';
import { Field } from '../components/Field';

// currentPassword is required only when the email is being changed. The
// schema can't see the loaded user's email, so the cross-field rule is
// enforced in the submit handler instead.
const profileSchema = z.object({
  displayName: z.string().min(1, 'Required'),
  email: z.string().email('Enter a valid email'),
  currentPassword: z.string().optional(),
});

type ProfileValues = z.infer<typeof profileSchema>;

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(8, 'At least 8 characters'),
    confirmNewPassword: z.string().min(1, 'Required'),
  })
  .refine((d) => d.newPassword === d.confirmNewPassword, {
    message: 'Passwords must match',
    path: ['confirmNewPassword'],
  });

type PasswordValues = z.infer<typeof passwordSchema>;

export function MyAccount() {
  const meQuery = useMe();
  const patchMe = usePatchMe();
  const changePassword = useChangeMyPassword();
  const [profileSavedAt, setProfileSavedAt] = useState<number | null>(null);
  const [passwordSavedAt, setPasswordSavedAt] = useState<number | null>(null);

  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { displayName: '', email: '', currentPassword: '' },
  });
  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  // Hydrate the profile form once `me` is loaded. We don't reset on every
  // re-render — only when `me.data?.id` flips — so the user's in-flight edits
  // aren't clobbered by a background refetch.
  useEffect(() => {
    if (meQuery.data) {
      profileForm.reset({
        displayName: meQuery.data.displayName,
        email: meQuery.data.email,
        currentPassword: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.data?.id]);

  useEffect(() => {
    if (profileSavedAt === null) return;
    const t = setTimeout(() => setProfileSavedAt(null), 2500);
    return () => clearTimeout(t);
  }, [profileSavedAt]);

  useEffect(() => {
    if (passwordSavedAt === null) return;
    const t = setTimeout(() => setPasswordSavedAt(null), 2500);
    return () => clearTimeout(t);
  }, [passwordSavedAt]);

  if (meQuery.isPending) return <div className="loading">Loading…</div>;
  if (!meQuery.data) return <div className="error">Could not load your account.</div>;
  const me = meQuery.data;

  const onProfileSubmit = profileForm.handleSubmit(async (values) => {
    profileForm.clearErrors('root');
    const emailChanged = values.email.toLowerCase() !== me.email;
    if (emailChanged && (!values.currentPassword || values.currentPassword.length === 0)) {
      profileForm.setError('currentPassword', { message: 'Required to change email' });
      return;
    }
    try {
      await patchMe.mutateAsync({
        displayName: values.displayName,
        email: emailChanged ? values.email : undefined,
        currentPassword: emailChanged ? values.currentPassword : undefined,
      });
      setProfileSavedAt(Date.now());
      profileForm.setValue('currentPassword', '');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_credentials') {
          profileForm.setError('currentPassword', { message: 'Password is incorrect' });
        } else if (err.code === 'email_taken') {
          profileForm.setError('email', { message: 'Email already in use' });
        } else {
          profileForm.setError('root', { message: 'Could not save changes.' });
        }
      } else {
        profileForm.setError('root', { message: "Couldn't reach the server." });
      }
    }
  });

  const onPasswordSubmit = passwordForm.handleSubmit(async (values) => {
    passwordForm.clearErrors('root');
    try {
      await changePassword.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      setPasswordSavedAt(Date.now());
      passwordForm.reset({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_credentials') {
        passwordForm.setError('currentPassword', { message: 'Password is incorrect' });
      } else if (err instanceof ApiError && err.status === 429) {
        passwordForm.setError('root', { message: 'Too many attempts. Try again in a minute.' });
      } else {
        passwordForm.setError('root', { message: 'Could not save changes.' });
      }
    }
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1>My account</h1>
      </div>

      <div className="card card-pad" style={{ marginBottom: 24 }}>
        <h2>Profile</h2>
        <form onSubmit={onProfileSubmit}>
          <Field label="Display name" error={profileForm.formState.errors.displayName?.message}>
            <input type="text" autoComplete="name" {...profileForm.register('displayName')} />
          </Field>
          <Field label="Email" error={profileForm.formState.errors.email?.message}>
            <input type="email" autoComplete="username" {...profileForm.register('email')} />
          </Field>
          <Field
            label="Current password (only required to change email)"
            error={profileForm.formState.errors.currentPassword?.message}
          >
            <input
              type="password"
              autoComplete="current-password"
              {...profileForm.register('currentPassword')}
            />
          </Field>
          {profileForm.formState.errors.root && (
            <div className="form-error">{profileForm.formState.errors.root.message}</div>
          )}
          <div className="page-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={profileForm.formState.isSubmitting}
            >
              {profileForm.formState.isSubmitting ? 'Saving…' : 'Save changes'}
            </button>
            {profileSavedAt !== null && <span className="micro">Saved.</span>}
          </div>
        </form>
      </div>

      <div className="card card-pad">
        <h2>Change password</h2>
        <form onSubmit={onPasswordSubmit}>
          <Field
            label="Current password"
            error={passwordForm.formState.errors.currentPassword?.message}
          >
            <input
              type="password"
              autoComplete="current-password"
              {...passwordForm.register('currentPassword')}
            />
          </Field>
          <Field
            label="New password"
            error={passwordForm.formState.errors.newPassword?.message}
            help="At least 8 characters."
          >
            <input
              type="password"
              autoComplete="new-password"
              {...passwordForm.register('newPassword')}
            />
          </Field>
          <Field
            label="Confirm new password"
            error={passwordForm.formState.errors.confirmNewPassword?.message}
          >
            <input
              type="password"
              autoComplete="new-password"
              {...passwordForm.register('confirmNewPassword')}
            />
          </Field>
          {passwordForm.formState.errors.root && (
            <div className="form-error">{passwordForm.formState.errors.root.message}</div>
          )}
          <div className="page-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={passwordForm.formState.isSubmitting}
            >
              {passwordForm.formState.isSubmitting ? 'Saving…' : 'Change password'}
            </button>
            {passwordSavedAt !== null && <span className="micro">Password updated.</span>}
          </div>
        </form>
      </div>
    </div>
  );
}
